import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
} from '@heroicons/react/outline';
import { toIntlDateTimeFormat } from '@zedoc/date';
import Tag from '../../../common/components/Tag';
import Card from '../../../components/Card';
import Table from '../../../components/lists/Table';
import {
  getAppointmentStatusType,
  INTERNAL_STATUS_ACTIVE,
  APPOINTMENT_STATUS_OPTION_ALL,
  CLINICIAN_OPTION_ALL,
  CLINICIAN_OPTIONS,
  getAppointmentStatusLabel,
  getAppointmentStatusOptions,
} from '../helpers';
import CalendarTimeView from '../../../components/CalendarTimeView';
import Layout from '../components/Layout';
import FormFieldText from '../../../components/forms/FormFieldText';
import FormFieldSearch from '../../../components/forms/FormFieldSearch';
import { AppointmentDetails } from '../../../components/dialogs/EpisodeOfCareDetails';
import useAppointments from '../hooks/useAppointments';
import useClinicians from '../hooks/useClinicians';

export const getAppointmentColumns = ({ language, t }) => [
  {
    key: 'projectName',
    title: t('forms:projectName.label'),
    render: (_, { context: { projectId, projectLabel } }) => {
      const hasError = !projectId;

      return (
        <span className={`${hasError ? 'text-danger-500' : ''}`}>
          {projectLabel || projectId || 'None assigned'}
        </span>
      );
    },
  },
  {
    key: 'recipientInformation',
    title: t('forms:recipientInformation.label'),
    render: (_, { patientId, context: { patientName } }) => {
      const hasPatientNameError = !patientName;
      const hasPatientIdError = !patientId;

      return (
        <div className="stack-0">
          <span className={`${hasPatientNameError ? 'text-danger-500' : ''}`}>
            {patientName || patientId || 'None assigned'}
          </span>
          <span
            className={`text-xs ${
              hasPatientIdError ? 'text-danger-500' : 'text-secondary'
            }`}
          >
            ID: {patientId || 'None assigned'}
          </span>
        </div>
      );
    },
  },
  {
    key: 'clinicianName',
    title: t('forms:clinician.label'),
    render: (_, { context: { clinicianName } }) => clinicianName,
  },
  {
    key: 'date',
    title: t('date'),
    render: (_, { start }) => {
      return (
        <div className="cluster-2 items-center">
          <CalendarIcon className="w-4 h-4" />
          <span>{toIntlDateTimeFormat(language, start)}</span>
        </div>
      );
    },
  },
  {
    key: 'time',
    title: t('time'),
    render: (_, { start, end }) => {
      return (
        <div className="cluster-2 items-center whitespace-nowrap">
          <ClockIcon className="w-4 h-4" />
          <span>
            {toIntlDateTimeFormat(language, start, {
              timeStyle: 'short',
            })}
            {' - '}
            {toIntlDateTimeFormat(language, end, {
              timeStyle: 'short',
            })}
          </span>
        </div>
      );
    },
  },
  {
    key: 'duration',
    title: t('duration'),
    render: (_, { start, end }) => <span>{(end - start) / 60000} min</span>,
  },
  {
    key: 'location',
    title: t('location'),
    render: (_, { context: { location } }) => {
      return (
        <div className="cluster-2 items-center whitespace-nowrap">
          <LocationMarkerIcon className="w-4 h-4" />
          <span>{location}</span>
        </div>
      );
    },
  },
  {
    key: 'appointmentStatus',
    title: t('forms:status.label'),
    render: (_, { status }) => {
      return (
        <Tag size="small" type={getAppointmentStatusType(status)} block>
          {getAppointmentStatusLabel({ status, t })}
        </Tag>
      );
    },
  },
  {
    key: 'type',
    title: t('type'),
    render: (_, { context: { type } }) => type,
  },
  {
    key: 'description',
    title: t('forms:description.label'),
    render: (_, { context: { description } }) => description,
  },
  {
    key: 'appointmentId',
    title: t('forms:appointmentId.label'),
  },
];

const Appointments = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [currentStatus, setCurrentStatus] = useState(
    APPOINTMENT_STATUS_OPTION_ALL,
  );
  const [patientName, setPatientName] = useState('');
  const [patientNameInput, setPatientNameInput] = useState('');
  const [clinicianId, setClinicianId] = useState(null);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Calendar date range state
  const [calendarDateRange, setCalendarDateRange] = useState(null);
  const [mode, setMode] = useState('table');

  const { clinicians, loading: cliniciansLoading } = useClinicians();

  const statusFilter =
    currentStatus !== APPOINTMENT_STATUS_OPTION_ALL ? currentStatus : undefined;

  // Table data fetch (paginated, with all filters)
  const { appointments, total, loading, error } = useAppointments({
    page: currentPage,
    pageSize,
    status: statusFilter,
    patientName: patientName || undefined,
    clinicianId: clinicianId || undefined,
  });

  // Calendar data fetch (date-filtered, with all filters, no pagination)
  const isCalendarMode = mode === 'calendar';
  const { appointments: calendarAppointments, loading: calendarLoading } =
    useAppointments(
      isCalendarMode
        ? {
            page: 1,
            pageSize: 500,
            status: statusFilter,
            patientName: patientName || undefined,
            clinicianId: clinicianId || undefined,
            startDate: calendarDateRange?.startDate,
            endDate: calendarDateRange?.endDate,
            view: 'calendar',
          }
        : { skip: true },
    );

  const handleCalendarDateRangeChange = useCallback(
    ({ startDate, endDate }) => {
      setCalendarDateRange({ startDate, endDate });
    },
    [],
  );

  const handlePageChange = useCallback(
    (page) => {
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    },
    [currentPage],
  );

  const handlePageSizeChange = useCallback(
    (current, newSize) => {
      const parsedPageSize =
        typeof newSize === 'string' ? parseInt(newSize, 10) : newSize;

      if (parsedPageSize !== pageSize) {
        setPageSize(parsedPageSize);
        setCurrentPage(1); // Reset to first page when page size changes
      }
    },
    [pageSize],
  );

  const pagination = useMemo(
    () => ({
      total,
      current: currentPage,
      pageSize,
      pageSizeOptions: [10, 15, 20],
      size: 'small',
      showSizeChanger: true,
      onChange: handlePageChange, // For page navigation
      onShowSizeChange: handlePageSizeChange, // For page size changes
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    }),
    [total, currentPage, pageSize, handlePageChange, handlePageSizeChange],
  );

  const handleStatusChange = (newStatus) => {
    setCurrentStatus(newStatus);
    setCurrentPage(1);
  };

  const handlePatientNameInputChange = (value) => {
    setPatientNameInput(value);
  };

  const commitPatientName = useCallback(() => {
    if (patientNameInput !== patientName) {
      setPatientName(patientNameInput);
      setCurrentPage(1);
    }
  }, [patientNameInput, patientName]);

  const handlePatientNameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        commitPatientName();
      }
    },
    [commitPatientName],
  );

  const handleClinicianChange = (newClinicianId) => {
    if (newClinicianId === CLINICIAN_OPTION_ALL) {
      setClinicianId(null);
    } else {
      const numericId =
        typeof newClinicianId === 'string'
          ? parseInt(newClinicianId, 10)
          : newClinicianId;
      setClinicianId(numericId);
    }
    setCurrentPage(1);
  };

  const clinicianOptions = useMemo(() => {
    const options = [
      { value: CLINICIAN_OPTION_ALL, label: t('all') },
      ...clinicians,
    ];
    return options;
  }, [clinicians, t]);

  const columns = useMemo(
    () => (t && language ? getAppointmentColumns({ language, t }) : []),
    [language, t],
  );

  return (
    <>
      <Layout
        title={t('appointments')}
        description={t('screens:episodesOfCare.appointments.description')}
        sidebar={{
          mode,
          onSetMode: setMode,
          statistics: (
            <>
              <Card data-testid="card-total" title={t('total')} bordered>
                <span
                  data-testid="card-total-value"
                  className="text-xl font-semibold"
                >
                  {total}
                </span>
              </Card>
              <Card
                data-testid="card-today"
                type="info"
                title={t('today')}
                bordered
              >
                <div className="cluster-2 items-baseline">
                  <span
                    data-testid="card-today-value"
                    className="text-xl font-semibold"
                  >
                    0
                  </span>
                  <span className="text-sm text-secondary">
                    {t('scheduledFor', {
                      date: toIntlDateTimeFormat(language, new Date()),
                    })}
                  </span>
                </div>
              </Card>
            </>
          ),
          filters: (
            <>
              <FormFieldSearch
                data-testid="filter-status"
                label={t('forms:status.label')}
                options={getAppointmentStatusOptions({ t })}
                input={{
                  value: currentStatus,
                  onChange: handleStatusChange,
                }}
                meta={{}}
              />
              <FormFieldText
                data-testid="filter-patient"
                label={t('forms:patient.label')}
                placeholder={t('forms:patient.placeholder')}
                input={{
                  value: patientNameInput,
                  onChange: handlePatientNameInputChange,
                  onBlur: commitPatientName,
                  onKeyDown: handlePatientNameKeyDown,
                }}
                meta={{}}
              />
              <FormFieldSearch
                data-testid="filter-clinician"
                label={t('forms:clinician.label')}
                options={clinicianOptions}
                input={{
                  value: clinicianId || CLINICIAN_OPTION_ALL,
                  onChange: handleClinicianChange,
                }}
                meta={{}}
                loading={cliniciansLoading}
              />
            </>
          ),
        }}
      >
        {mode === 'table' && (
          <Card className="stack-4" bordered>
            <Table
              title={t('appointmentsWithCount', {
                count: total,
              })}
              dataSource={appointments}
              columns={columns}
              pagination={pagination}
              loading={loading}
              onRow={(appointment) => ({
                onClick: () => setCurrentAppointment(appointment),
              })}
            />
          </Card>
        )}
        {mode === 'calendar' && (
          <CalendarTimeView
            className="h-full"
            events={calendarAppointments}
            isLoading={calendarLoading}
            onDateRangeChange={handleCalendarDateRangeChange}
          />
        )}
      </Layout>
      <AppointmentDetails
        appointment={currentAppointment}
        visible={!!currentAppointment}
        onCancel={() => setCurrentAppointment(null)}
      />
    </>
  );
};

export default Appointments;
